import { MapPin, DollarSign, Building2, TrendingUp, Home, Layers, Landmark, Scale, Calendar, Users } from 'lucide-react';

const img = (id) => `https://images.unsplash.com/photo-${id}?w=800&q=85`;

const createSuburbArticles = (suburb, suburbSlug, config) => {
  const { distance, keyInfra, zones, mainStreets, priceRange, keyDriver, uniqueFactor, comparison, nearbyAmenity } = config;
  return [
    {
      id: `${suburbSlug}-property-development-site-guide`,
      category: 'Local Guide',
      title: `Is Your ${suburb} Property a Development Site? Signs to Look For`,
      excerpt: `${suburb} is attracting growing developer interest. Here's how to tell if your property has development potential.`,
      date: '2026-04-06',
      readTime: '7 min read',
      image: img('1697767217116-465fa27e4521'),
      icon: MapPin,
      metaDescription: `Is your ${suburb} property a development site? Learn the signs that your land has development potential in this Brisbane suburb ${distance} from the CBD.`,
      content: `
## ${suburb}: Development Potential

${suburb} sits ${distance} from the Brisbane CBD. ${uniqueFactor}

If you own property in ${suburb}, there's a good chance it could be worth significantly more as a development site than as a residential home.

## Key Signs Your ${suburb} Property Has Development Value

### 1. Your Zoning Allows Higher Density

Under the Brisbane City Plan 2014, ${suburb} contains several zones:

| Zone | Typical Height | What Can Be Built |
|---|---|---|
${zones.map(z => `| ${z.name} | ${z.height} | ${z.product} |`).join('\n')}

Check your specific zoning at [cityplan.brisbane.qld.gov.au](https://cityplan.brisbane.qld.gov.au/).

### 2. Your Block is Suitable for Development

Developers in ${suburb} look for:
- **Blocks of 400m²+** in medium-to-high density zones
- **Wide frontage** (15m+ preferred) for efficient building design
- **Corner blocks** with dual street frontage — these command a 20-35% premium
- **Flat or gently sloping** topography for straightforward construction

### 3. You're Near Key Infrastructure

${keyInfra}

### 4. Developers Have Already Approached You

If you've received unsolicited letters or approaches from developers, it confirms market interest — but never accept without independent advice. A specialist agent creates competition that drives 15-30% higher prices.

> **Pro tip**: Properties in ${suburb} that would sell for a standard residential price regularly achieve 40-120% more when marketed as development sites to qualified developer buyers.

## What's Driving Developer Demand in ${suburb}

- ${keyDriver}
- Strong end-buyer demand for inner-city apartment and townhouse living
- Limited remaining development sites creating scarcity premium
- ${nearbyAmenity}
- South East Queensland's sustained population growth and housing undersupply

## How to Find Out Your Property's Potential

ACRES provides complimentary development site assessments for ${suburb} property owners covering zoning, constraints, estimated yield, and realistic price expectations.

## Frequently Asked Questions

**Q: How much more could my ${suburb} property be worth as a development site?**
Properties in medium-to-high density zones typically sell for 40-120% above standard residential value. The premium depends on zoning, block size, and site characteristics.

**Q: Do I need to get development approval before selling?**
No. Most developers prefer to design their own project. A site with an existing DA can attract a 10-20% premium but isn't required.

**Q: How long will it take to sell my ${suburb} property as a development site?**
Development site sales typically take 2-4 months to secure a contract, with settlement periods of 6-18 months depending on conditions.

---

*ACRES provides complimentary development site assessments in ${suburb}. Call 07 3096 0542 or email daniel@acres.au.*
      `,
      faq: [
        { q: `Is my ${suburb} property a development site?`, a: `If your property is zoned for medium or high density in ${suburb}, it likely has significant development value. Corner blocks and lots over 500m² are particularly attractive to developers.` },
        { q: `How much more is my ${suburb} land worth to a developer?`, a: `Properties in medium-to-high density zones can be worth 40-120% more as development sites depending on zoning, block size, and site characteristics.` },
        { q: `Are developers actively buying in ${suburb}?`, a: `Yes. ${suburb} is an active development site market driven by ${keyDriver.toLowerCase()} and sustained end-buyer demand.` },
      ],
    },
    {
      id: `${suburbSlug}-land-prices-what-developers-pay`,
      category: 'Local Guide',
      title: `What Developers Are Paying for Land in ${suburb}`,
      excerpt: `Current pricing benchmarks for development sites in ${suburb} and the factors driving land values.`,
      date: '2026-04-04',
      readTime: '6 min read',
      image: img('1672643405411-10cdbebfc3df'),
      icon: DollarSign,
      metaDescription: `What are developers paying for land in ${suburb} Brisbane? Current development site pricing, benchmarks, and what drives land values.`,
      content: `
## ${suburb} Development Site Pricing

${suburb} development sites attract strong demand from developers targeting Brisbane's inner-ring growth corridor. ${uniqueFactor}

## Current Price Benchmarks

| Site Type | Typical Size | Price Range | Price per m² |
|---|---|---|---|
${priceRange.map(p => `| ${p.type} | ${p.size} | ${p.range} | ${p.perm2} |`).join('\n')}

These figures represent what developers will pay — often significantly above standard residential market value.

## What Drives Price Variation in ${suburb}

### Zoning and Height
The single biggest price driver. Higher density zoning allows more apartments, generating more revenue, which means developers can pay more for the land.

### Location Within the Suburb
Properties on or near ${mainStreets} command premiums due to higher density zoning, better transport access, and stronger end-buyer appeal.

### Site Configuration
Wide frontage (15m+), flat topography, and corner positions increase value. Narrow, deep blocks or steeply sloping sites attract fewer buyers.

### Existing Approvals
A site with a current DA typically sells for 10-20% more than an unapproved site, as the buyer avoids months of approval risk and holding costs.

## How ${suburb} Compares

${comparison}

> **Key insight**: Properties that would sell for a standard residential price regularly achieve significant premiums when marketed correctly as development sites.

## Frequently Asked Questions

**Q: Are ${suburb} development site prices still rising?**
Prices have shown consistent growth driven by limited supply and strong demand. Structural factors support continued pricing strength.

**Q: How do I know if a developer's offer is fair?**
Never accept an unsolicited offer without independent advice. A specialist agent tests the broader market and creates competitive tension.

**Q: How do I get an accurate price for my ${suburb} site?**
Contact ACRES for a complimentary development site appraisal that considers zoning, yield, feasibility, and current demand.

---

*For a confidential ${suburb} development site appraisal, contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `What are developers paying for land in ${suburb}?`, a: `Pricing varies by zone and site characteristics. ${priceRange[1] ? `${priceRange[1].type} sites typically sell for ${priceRange[1].range}.` : 'Contact ACRES for current benchmarks.'}` },
        { q: `Are ${suburb} development site prices still rising?`, a: `Yes. Limited supply and strong demand continue to support price growth in ${suburb}.` },
        { q: `How much more will a developer pay vs a home buyer in ${suburb}?`, a: `Developers typically pay 40-120% above residential value depending on zoning. A specialist agent can provide an accurate assessment.` },
      ],
    },
    {
      id: `${suburbSlug}-zoning-guide-what-you-can-build`,
      category: 'Local Guide',
      title: `${suburb} Zoning Guide: What You Can Build on Your Block`,
      excerpt: `A plain-English breakdown of ${suburb}'s zoning and what it means for your property's development value.`,
      date: '2026-04-02',
      readTime: '8 min read',
      image: img('1606509463400-5ce6440c53e9'),
      icon: Scale,
      metaDescription: `${suburb} Brisbane zoning guide for property owners. Understanding your zoning, building heights, and development potential under Brisbane City Plan 2014.`,
      content: `
## Understanding ${suburb}'s Zoning

${suburb}'s zoning is governed by the **Brisbane City Plan 2014** and any applicable neighbourhood plan provisions. Your zoning determines what can be built on your property — and how much a developer will pay for it.

## Zones in ${suburb}

${zones.map(z => `### ${z.name}
- **Height**: ${z.height}
- **What you can build**: ${z.product}
- **Development premium**: ${z.premium || 'Varies by site'}
`).join('\n')}

## How Zoning Affects Your Property Value

| Scenario | Zoning | Potential Yield | Value Impact |
|---|---|---|---|
| Standard home | Low Density | 1 dwelling | Residential baseline |
| Townhouse site | Low-Medium Density | 3-5 townhouses | 30-50% premium |
| Apartment site | Medium Density | 8-15 apartments | 50-100% premium |
| Tower site | High Density | 20-50+ apartments | 100-200%+ premium |

Same block size — dramatically different values based on zoning alone.

## Key Overlays in ${suburb}

- **Flood overlay**: Check for flood mapping near waterways — affects ground-floor design
- **Heritage/Character overlay**: May restrict demolition of pre-1947 houses and limit height
- **Infrastructure easements**: Sewer, stormwater, or power may reduce developable area

## How to Check Your ${suburb} Zoning

1. Visit [cityplan.brisbane.qld.gov.au](https://cityplan.brisbane.qld.gov.au/)
2. Search your ${suburb} address
3. Check **Zoning**, **Overlays**, and **Neighbourhood Plans** layers

> **Pro tip**: The base zoning tells you what's theoretically possible, but overlays and neighbourhood plan provisions can modify the outcome. A professional assessment gives you the complete picture.

## Frequently Asked Questions

**Q: How do I find my exact zoning in ${suburb}?**
Use Brisbane City Plan Interactive Mapping. Search your address and check the Zoning layer. For a professional interpretation, contact ACRES.

**Q: Can my ${suburb} zoning change?**
Yes. Council reviews the City Plan periodically. Properties near infrastructure are candidates for future upzoning.

**Q: Does higher zoning guarantee maximum height?**
Not always. Overlays and neighbourhood plan provisions may limit achievable height. A planning assessment confirms realistic potential.

---

*Not sure what your ${suburb} zoning means? ACRES provides free assessments. Call 07 3096 0542.*
      `,
      faq: [
        { q: `What zoning does my ${suburb} property have?`, a: `${suburb} has a mix of residential zones. Check cityplan.brisbane.qld.gov.au for your specific property. ACRES provides free zoning assessments.` },
        { q: `What can I build on my ${suburb} block?`, a: `It depends on your zone. ${zones[0] ? `${zones[0].name} allows ${zones[0].product.toLowerCase()}.` : ''} Higher zones allow apartments and mixed-use developments.` },
        { q: `Could my ${suburb} property be upzoned?`, a: `Properties near infrastructure and transport are candidates for upzoning in future City Plan reviews.` },
      ],
    },
    {
      id: `${suburbSlug}-infrastructure-driving-property-values`,
      category: 'Local Guide',
      title: `How Infrastructure Is Driving ${suburb} Property Values`,
      excerpt: `Key infrastructure and amenity driving development demand and property values in ${suburb}.`,
      date: '2026-04-28',
      readTime: '6 min read',
      image: img('1656501375517-67129e3eed14'),
      icon: TrendingUp,
      metaDescription: `How infrastructure and amenity are driving ${suburb} Brisbane property values. Impact on land prices, development demand, and what it means for property owners.`,
      content: `
## Infrastructure Driving ${suburb}'s Growth

${keyInfra}

For property owners in ${suburb}, infrastructure investment is the single most reliable indicator of future value growth.

## How Infrastructure Affects Development Site Values

| Infrastructure Type | Typical Value Uplift | Mechanism |
|---|---|---|
| Rail station (within 800m) | 10-25% | Higher density + end-buyer premium |
| Bus rapid transit / busway | 5-15% | Improved connectivity drives demand |
| Major retail / commercial | 5-10% | Lifestyle amenity attracts residents |
| Hospital / University | 8-15% | Employment + staff housing demand |
| Road upgrades | 3-8% | Improved access and connectivity |

For development sites, the uplift is amplified because infrastructure proximity triggers higher density allowances and stronger end-product pricing.

## What This Means for ${suburb} Property Owners

The infrastructure driving ${suburb}'s growth creates sustained demand from developers who need sites to deliver housing. If you own property in ${suburb}:

- Your land is likely worth more now than it was 3 years ago
- Development site premiums above residential value are increasing
- Multiple buyer types are competing for available sites
- Timing is favourable for sellers

> **Important**: Infrastructure-driven value growth typically continues for 5-10 years after project completion as the surrounding precinct matures.

## Frequently Asked Questions

**Q: Has infrastructure already increased ${suburb} property values?**
Yes. Values have risen as infrastructure projects progress and connectivity improves. Development site premiums are particularly strong.

**Q: Will values keep rising?**
Historical evidence suggests values continue rising 5-10 years post-completion as precincts mature.

**Q: Should I sell now or wait?**
Current conditions are favourable. ACRES can advise on optimal timing for your circumstances.

---

*Understand how infrastructure affects your ${suburb} property. Contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `How does infrastructure affect ${suburb} property values?`, a: `Major infrastructure increases surrounding property values by 5-25%. Development sites near transport and amenity see the greatest uplift.` },
        { q: `Will ${suburb} property values keep rising?`, a: `Infrastructure-driven growth typically continues 5-10 years after completion. Current fundamentals remain strong for ${suburb}.` },
        { q: `Should I sell my ${suburb} property now or wait?`, a: `Current conditions are favourable with strong demand. The optimal timing depends on your circumstances — ACRES can advise.` },
      ],
    },
    {
      id: `selling-${suburbSlug}-home-to-developer-guide`,
      category: 'Local Guide',
      title: `Selling Your ${suburb} Home to a Developer: A Complete Guide`,
      excerpt: `Step-by-step process for selling your ${suburb} property as a development site for maximum value.`,
      date: '2026-04-28',
      readTime: '7 min read',
      image: img('1513880989635-6eb491ce7f5b'),
      icon: Home,
      metaDescription: `How to sell your ${suburb} home to a developer. Complete guide covering preparation, pricing, agent selection, and achieving the best development site price.`,
      content: `
## Why ${suburb} Sellers Should Consider a Development Sale

If your ${suburb} property sits on land zoned for medium or high density, selling to a developer could unlock 40-120% more value than a standard residential sale.

## Step 1: Understand Your Development Potential

- **Check your zoning** at [cityplan.brisbane.qld.gov.au](https://cityplan.brisbane.qld.gov.au/)
- **Measure your block** — lot size, frontage width, and shape affect yield
- **Note key features** — corner position, transport proximity, main road frontage
- **Identify constraints** — overlays, easements, heritage or character listings

## Step 2: Choose a Specialist Agent

Look for:
- Development site experience in ${suburb} and Brisbane's inner ring
- An active database of developer buyers currently seeking sites
- Knowledge of recent comparable sales and current market conditions
- A clear marketing strategy targeting developer buyers

> **Warning**: Unsolicited developer offers are almost always below market value. A specialist agent achieves 15-30% more through competitive tension.

## Step 3: Prepare Your Property

### Essential:
- **Boundary survey** ($2,000-$4,000)
- **Title search** and encumbrance review
- **Planning summary** — zoning, height limits, estimated yield

### Valuable additions:
- **Town planning assessment** ($3,000-$5,000) — confirms realistic development potential
- **Concept design** ($5,000-$10,000) — makes the opportunity tangible for buyers

Don't invest in house renovations — developers will demolish regardless.

## Step 4: Market Through an EOI Campaign

1. Agent contacts developer database directly
2. Information memorandum prepared with site details
3. Site inspections with qualified buyers
4. Offer deadline creates competitive tension
5. Negotiate with strongest offers

## Step 5: Negotiate Terms

Beyond price, negotiate:
- **Deposit**: 5-10% (push for 10%)
- **Conditions**: DA, finance, due diligence with clear timeframes
- **Settlement**: 6-18 months typical
- **Sunset clause**: Deadline for condition satisfaction

## Frequently Asked Questions

**Q: Can I stay in my ${suburb} home during the sale process?**
Yes. You remain in the property throughout marketing and the conditional period. Settlement is typically 6-18 months after contract.

**Q: How long does the process take?**
2-4 months to contract, then 6-18 months conditional period, then 4-8 weeks to settlement.

**Q: What commission does a development site agent charge?**
Typically 1.5-2.5%. Given the significantly higher prices achieved, the net outcome is much better.

---

*Ready to explore your ${suburb} property's potential? Call ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `How do I sell my ${suburb} home to a developer?`, a: `Engage a specialist agent, prepare with survey and planning assessment, market through an EOI to create competitive tension, and negotiate terms as well as price.` },
        { q: `Can I live in my ${suburb} home while selling to a developer?`, a: `Yes. You remain in the property throughout. Settlement is typically 6-18 months after contract.` },
        { q: `What commission does a development site agent charge in ${suburb}?`, a: `Typically 1.5-2.5%. Given premiums of 40-120% above residential value, the net outcome is significantly better.` },
      ],
    },
    {
      id: `${suburbSlug}-development-hotspots`,
      category: 'Local Guide',
      title: `${suburb} Development Hotspots: Where Developers Want to Buy`,
      excerpt: `The specific streets and pockets within ${suburb} attracting the strongest developer demand.`,
      date: '2026-04-26',
      readTime: '5 min read',
      image: img('1730329944265-ab697e1a631c'),
      icon: Layers,
      metaDescription: `${suburb} development hotspots. Which streets and areas are attracting the most developer interest in ${suburb} Brisbane?`,
      content: `
## Where Developers Are Buying in ${suburb}

Not all streets in ${suburb} are equal for development demand. Developers target specific pockets based on zoning, infrastructure, and end-buyer appeal.

## Primary Hotspots

Properties along and near **${mainStreets}** are the most sought-after, offering:
- Higher density zoning and height allowances
- Transport connectivity and amenity access
- Strong end-buyer demand for completed apartments and townhouses
- Established commercial and lifestyle precincts

## Secondary Hotspots

Quieter residential streets in MDR zones offer:
- More affordable entry points for boutique developers
- Appeal to owner-occupier end-buyers seeking quieter locations
- Potential for medium-density townhouse and apartment projects

## What Makes a Street a Hotspot

| Factor | Impact on Developer Demand |
|---|---|
| HDR or Centre zoning | Very High — maximum yield |
| MDR zoning | High — sweet spot for many projects |
| Corner position | High — design and access advantages |
| Transport proximity | High — end-buyer premium |
| LMR with large lots | Moderate — townhouse potential |
| Character/Heritage overlay | Lower — restrictions limit options |

## Emerging Opportunities

Watch for streets where council has increased density, adjacent streets have seen successful developments, or infrastructure projects are approaching completion. Early sellers in emerging hotspots often achieve the best outcomes.

## Frequently Asked Questions

**Q: My ${suburb} property isn't on a main road — do developers want it?**
Yes. Many developers prefer quieter streets for residential projects. If your zoning supports medium density, your property has development value.

**Q: Are developers only buying large sites?**
No. Standard 400-600m² blocks in suitable zones attract strong interest. Site consolidation is also common.

**Q: How do I know if my street has developer demand?**
Contact ACRES for a free assessment. We monitor developer activity across every ${suburb} street.

---

*Is your ${suburb} street a development hotspot? Contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `Which ${suburb} streets are developers buying on?`, a: `Properties along ${mainStreets} see the strongest demand. Quieter MDR-zoned streets also attract boutique developers.` },
        { q: `Do developers buy on quiet streets in ${suburb}?`, a: `Yes. Many developers prefer quieter streets for residential projects targeting owner-occupiers.` },
        { q: `Are developers only buying large blocks in ${suburb}?`, a: `No. Standard 400-600m² blocks in MDR and HDR zones are actively sought. Consolidation is also common.` },
      ],
    },
    {
      id: `future-of-${suburbSlug}-council-plans`,
      category: 'Local Guide',
      title: `The Future of ${suburb}: Council Plans & What They Mean for Landowners`,
      excerpt: `How evolving council policy and infrastructure investment are shaping ${suburb}'s development future.`,
      date: '2026-04-24',
      readTime: '6 min read',
      image: img('1672626881472-b96d3eb5445e'),
      icon: Landmark,
      metaDescription: `The future of ${suburb} Brisbane. How council planning changes and infrastructure investment are shaping the suburb for property owners.`,
      content: `
## ${suburb}'s Evolving Landscape

${suburb} continues to evolve as Brisbane grows and demand for well-located housing intensifies. Council planning, infrastructure, and demographic shifts are shaping the suburb's future — and your property's value.

## What's Driving Change

### Population Growth
South East Queensland adds approximately 100,000 people per year. Inner-city suburbs like ${suburb} absorb significant growth through medium and high-density development.

### Infrastructure Investment
${keyInfra}

### Planning Evolution
Council periodically reviews the City Plan. ${suburb} may see continued refinement of density provisions along transport corridors and near activity centres.

## What This Means for Property Owners

| Your Property Location | Likely Trajectory | Action to Consider |
|---|---|---|
| Near transport corridor | Density increases likely | Strong position — market favourable |
| MDR zone, no constraints | Stable to increasing | Good conditions for development sale |
| Character/heritage area | Likely restricted | Assess best sale approach |
| Near emerging infrastructure | Potential upzoning | Consider timing vs project completion |

## Looking Ahead

| Timeframe | Expected Impact |
|---|---|
| 2025-2027 | Infrastructure benefits materialise |
| 2027-2030 | Precinct maturation, continued demand |
| 2030-2035 | Established premium status |

## Frequently Asked Questions

**Q: Will ${suburb} be further upzoned?**
Possible near transport and growth corridors. Council reviews the City Plan periodically.

**Q: Should I wait for planning changes?**
Current zoning supports strong values. Waiting carries uncertainty. ACRES can advise on timing.

**Q: How do planning changes affect value?**
Density increases raise development site values. Character restrictions maintain current levels.

---

*Understand ${suburb}'s future and your property's potential. Contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `Will ${suburb} be further upzoned?`, a: `Possible near transport corridors. Council reviews the City Plan periodically and population growth supports density increases.` },
        { q: `What does the future hold for ${suburb} property values?`, a: `Strong fundamentals including population growth, infrastructure, and limited supply support continued appreciation.` },
        { q: `Should I sell my ${suburb} property now or wait?`, a: `Current conditions are favourable. Waiting carries uncertainty. ACRES can advise on timing.` },
      ],
    },
    {
      id: `${suburbSlug}-corner-blocks-large-lots`,
      category: 'Local Guide',
      title: `${suburb} Corner Blocks & Large Lots: Why Developers Pay More`,
      excerpt: `Corner blocks and larger lots in ${suburb} command significant premiums from developer buyers.`,
      date: '2026-04-22',
      readTime: '5 min read',
      image: img('1557518016-95747c0eef63'),
      icon: Building2,
      metaDescription: `Why developers pay more for corner blocks and large lots in ${suburb} Brisbane. Premium pricing and what makes your block attractive.`,
      content: `
## The Premium for Corners and Large Lots in ${suburb}

Corner blocks and large lots command significant premiums because they offer developers design, yield, and market advantages.

## Why Corner Blocks Are Worth More

- **Dual street frontage** — separate pedestrian and vehicle access
- **Better apartment layouts** — more natural light and cross-ventilation
- **More attractive building forms** — engaging with both streetscapes
- **Higher density allowances** in some planning provisions

Corner blocks in ${suburb} typically command a **20-35% premium** over comparable mid-block sites.

## Why Large Lots Are Valuable

Large lots (700m²+) offer:
- Greater yield on a single title
- No amalgamation risk or neighbour negotiations
- More efficient building design
- Faster project commencement

## Site Consolidation

If you own property adjacent to another potential development site, a coordinated sale can increase total value by 10-20%. ACRES facilitates these discussions confidentially.

## Frequently Asked Questions

**Q: How much more is a corner block worth in ${suburb}?**
Typically 20-35% more than a comparable mid-block site in the same zone.

**Q: Is my standard block still a development site?**
Yes, if zoned for medium or high density. Even standard blocks attract developer interest.

**Q: Can I coordinate a sale with my neighbour?**
Yes. ACRES facilitates confidential multi-vendor sales for site consolidation.

---

*Own a corner block or large lot in ${suburb}? Contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `How much more is a corner block worth in ${suburb}?`, a: `Typically 20-35% more than mid-block sites in the same zone.` },
        { q: `Are large lots particularly valuable in ${suburb}?`, a: `Yes. Large lots (700m²+) are rare and command significant premiums.` },
        { q: `Can I sell my ${suburb} property with my neighbour's?`, a: `Yes. ACRES facilitates confidential multi-vendor sales. Combined value is typically 10-20% higher.` },
      ],
    },
    {
      id: `${suburbSlug}-property-market-2025`,
      category: 'Local Guide',
      title: `${suburb} Property Market 2026: Development Site Demand & Pricing`,
      excerpt: `Current market conditions for ${suburb} development sites and where the market is heading.`,
      date: '2026-04-20',
      readTime: '6 min read',
      image: img('1659563159297-6f724e125026'),
      icon: Calendar,
      metaDescription: `${suburb} property market 2025. Development site demand, pricing trends, and market outlook for sellers.`,
      content: `
## Market Overview: ${suburb} 2025

${suburb}'s development site market in 2026 features strong demand and limited supply. The suburb's positioning, transport connectivity, and lifestyle appeal continue to attract developers.

## Who's Buying
- Boutique developers building 15-40 apartment or townhouse projects
- National developers seeking medium-to-large sites
- Build-to-rent operators seeking well-located rental housing sites
- Townhouse specialists targeting inner-city terrace home demand

## Market Conditions

| Indicator | Status | Implication |
|---|---|---|
| Developer demand | Strong | Multiple buyers for quality sites |
| Site supply | Limited | Scarcity supporting firm pricing |
| End-buyer demand | Resilient | Developer confidence in sales |
| Construction costs | Elevated | Moderating margins but not deterring acquisitions |
| Off-market activity | Increasing | Many transactions before public marketing |

## Outlook

Current conditions are favourable for sellers:
- Strong demand fundamentals
- Limited remaining supply
- Infrastructure investment continuing
- End-buyer market resilient

> **Bottom line**: ${suburb} is a seller's market for development sites. Well-prepared properties marketed through specialist agents achieve premium outcomes.

## Frequently Asked Questions

**Q: Is 2025 a good time to sell in ${suburb}?**
Yes. Demand is strong, supply is limited, and fundamentals continue to improve.

**Q: Will prices keep rising?**
Structural factors support continued pricing strength. Limited supply and sustained demand provide a strong floor.

**Q: How many developers are active in ${suburb}?**
Multiple national and local developers are actively seeking sites. Competition is strong.

---

*For a current ${suburb} market assessment, contact ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `Is 2025 a good time to sell in ${suburb}?`, a: `Yes. Strong demand, limited supply, and improving fundamentals make conditions favourable.` },
        { q: `Are ${suburb} development site prices still rising?`, a: `Prices are firm to rising. Limited supply and sustained demand support continued strength.` },
        { q: `How many developers are buying in ${suburb}?`, a: `Multiple developers are actively seeking sites. Competition for well-located properties is strong.` },
      ],
    },
    {
      id: `downsizing-${suburbSlug}-selling-developer`,
      category: 'Local Guide',
      title: `Downsizing in ${suburb}: How Selling to a Developer Funds Your Next Chapter`,
      excerpt: `Long-term ${suburb} homeowners could unlock significant value by selling to a developer rather than a home buyer.`,
      date: '2026-04-18',
      readTime: '6 min read',
      image: img('1656501382301-9b560230018a'),
      icon: Users,
      metaDescription: `Downsizing in ${suburb} Brisbane by selling to a developer. How long-term homeowners can unlock premium development value to fund retirement.`,
      content: `
## Your ${suburb} Home Could Fund a Comfortable Retirement

If you've owned your ${suburb} home for many years, the suburb's evolution means your property could be worth significantly more to a developer than to a traditional home buyer.

## The Opportunity

Properties in ${suburb}'s medium-to-high density zones regularly sell for **40-120% above standard residential value** as development sites. The premium could mean hundreds of thousands of additional dollars.

## Tax Benefits

If the property has been your principal residence, the capital gain is generally **exempt from CGT**. The Downsizer Contribution allows individuals aged 55+ to contribute up to $300,000 ($600,000 per couple) into superannuation.

## A Typical ${suburb} Scenario

| Factor | Standard Sale | Developer Sale |
|---|---|---|
| Property | 3-bed home on 600m² (MDR zone) | Same property |
| Buyer type | Home buyer | Developer |
| Sale price | $950,000 | $1,500,000 |
| Net difference | — | **+$550,000** |

## Common Concerns

### "I don't want to leave ${suburb}"
You don't have to. Many downsizers purchase a nearby apartment and stay in the community.

### "The process sounds complicated"
A specialist agent manages everything. You continue living in your home throughout.

### "How long before I need to move?"
Settlement is typically 6-18 months after contract — plenty of time to plan.

## Next Steps

1. Request a free, confidential assessment from ACRES
2. Compare development vs residential value
3. Take your time — no rush, just information
4. Plan your next chapter with financial clarity

## Frequently Asked Questions

**Q: How much more could I get selling to a developer?**
Properties in suitable zones typically sell for 40-120% above residential value.

**Q: Do I pay tax on the sale?**
If it's been your principal residence, the gain is generally exempt from CGT.

**Q: Can I stay in ${suburb}?**
Yes. Many downsizers purchase a nearby apartment while banking the premium.

---

*Considering downsizing from your ${suburb} home? Call ACRES on 07 3096 0542.*
      `,
      faq: [
        { q: `How much more could I get selling my ${suburb} home to a developer?`, a: `Properties in medium-to-high density zones typically sell for 40-120% above residential value.` },
        { q: `Do I pay CGT selling my ${suburb} home?`, a: `If it's been your principal residence, the gain is generally exempt from CGT.` },
        { q: `Can I stay in ${suburb} after selling?`, a: `Yes. Many downsizers purchase a nearby apartment and stay in the community.` },
      ],
    },
  ];
};

// ─── BATCH 3 SUBURBS ────────────────────────────────────────────────────────

export const miltonArticles = createSuburbArticles('Milton', 'milton', {
  distance: '2km west of',
  keyInfra: 'Milton train station provides direct CBD access. Park Road is one of Brisbane\'s premier dining precincts. Suncorp Stadium (capacity 52,500) anchors the southern boundary. Coronation Drive provides a scenic river-road corridor to the CBD with premium apartment frontage.',
  zones: [
    { name: 'Medium Density Residential', height: '4-6 storeys', product: 'Walk-up apartments, townhouses', premium: 'Significant — 50-80% above residential' },
    { name: 'High Density Residential', height: '8-15+ storeys', product: 'Apartment towers along Coronation Dr', premium: 'Very high — river corridor premium' },
    { name: 'Centre / Mixed Use', height: '10-20+ storeys', product: 'Mixed-use towers, commercial + residential', premium: 'Maximum — Park Road/station precinct' },
    { name: 'Sport and Recreation', height: 'Varies', product: 'Stadium and event facilities', premium: 'N/A — special purpose' },
  ],
  mainStreets: 'Park Road, Coronation Drive, Baroona Road, and Cribb Street',
  priceRange: [
    { type: 'Standard house (MDR)', size: '400-600m²', range: '$1.1M - $1.8M', perm2: '$2,400 - $3,200' },
    { type: 'HDR site (Coronation Dr)', size: '600-1,000m²', range: '$2.5M - $5.5M', perm2: '$3,500 - $5,500' },
    { type: 'Centre zone (Park Rd)', size: '800m²+', range: '$3.5M - $10M+', perm2: '$4,000 - $7,000+' },
  ],
  keyDriver: 'Park Road dining precinct, Suncorp Stadium event traffic, Milton train station, and Coronation Drive riverfront positioning',
  uniqueFactor: 'Milton is defined by two powerful assets: Park Road — one of Brisbane\'s most celebrated dining and lifestyle strips — and the Coronation Drive riverfront. Combined with Suncorp Stadium and direct rail access, Milton offers a unique urban lifestyle that drives premium apartment demand.',
  comparison: 'Milton commands strong pricing comparable to South Brisbane and West End. Coronation Drive riverfront sites are among the most valuable in Brisbane\'s western corridor.',
  nearbyAmenity: 'Park Road dining precinct, Suncorp Stadium, Coronation Drive riverwalk, and Milton train station',
});

export const paddingtonArticles = createSuburbArticles('Paddington', 'paddington', {
  distance: '3km west of',
  keyInfra: 'Given Terrace is Paddington\'s bustling commercial and dining strip. Rosalie Village provides a secondary retail centre. The suburb benefits from proximity to Milton and Suncorp Stadium. Bus routes along Given Terrace and Latrobe Terrace provide CBD connectivity.',
  zones: [
    { name: 'Character Residential', height: '2 storeys (strict)', product: 'Sympathetic renovations, limited infill', premium: 'Limited — extensive character protections' },
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 25-45% above residential' },
    { name: 'Medium Density Residential', height: '4-5 storeys', product: 'Walk-up apartments', premium: 'Significant — 50-75% above residential' },
    { name: 'Centre / Mixed Use', height: '4-8 storeys', product: 'Mixed-use along Given Terrace', premium: 'High — lifestyle precinct premium' },
  ],
  mainStreets: 'Given Terrace, Latrobe Terrace, Caxton Street, and Enoggera Terrace',
  priceRange: [
    { type: 'Character house lot', size: '400-600m²', range: '$1.2M - $2.2M (residential)', perm2: 'N/A — character protected' },
    { type: 'MDR site (no overlay)', size: '500-700m²', range: '$1.3M - $2.2M', perm2: '$2,400 - $3,200' },
    { type: 'Centre/Mixed Use (Given Tce)', size: '400-800m²', range: '$2.0M - $4.5M+', perm2: '$3,500 - $5,500+' },
  ],
  keyDriver: 'Premium residential address with Given Terrace lifestyle, heritage character appeal, and strong family and professional demand',
  uniqueFactor: 'Paddington is one of Brisbane\'s most iconic character suburbs — a hillside precinct of heritage Queenslanders, tree-lined streets, and the vibrant Given Terrace dining and retail strip. Extensive character protections limit development in many areas, but pockets of MDR and commercial zoning along the main terraces offer significant opportunities.',
  comparison: 'Paddington\'s character protections make development sites rare and valuable. Properties without character overlay in MDR zones command premiums reflecting the suburb\'s prestige positioning.',
  nearbyAmenity: 'Given Terrace dining/retail, Rosalie Village, Suncorp Stadium, and Paddington heritage streetscapes',
});

export const redHillArticles = createSuburbArticles('Red Hill', 'red-hill', {
  distance: '2.5km north-west of',
  keyInfra: 'Red Hill\'s elevated position provides panoramic city and river views from many properties. Proximity to Paddington (Given Terrace), Kelvin Grove Urban Village, and the CBD makes it well-connected. Bus routes along Waterworks Road and Musgrave Road provide CBD access. The Inner City Bypass is accessible nearby.',
  zones: [
    { name: 'Character Residential', height: '2 storeys (restricted)', product: 'Sympathetic renovations, character-sensitive infill', premium: 'Limited — character protections apply' },
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 30-50% above residential' },
    { name: 'Medium Density Residential', height: '4-5 storeys', product: 'Walk-up apartments', premium: 'Significant — 50-80% above residential' },
    { name: 'Centre (limited)', height: '4-6 storeys', product: 'Mixed-use on Waterworks Road', premium: 'High — views and location premium' },
  ],
  mainStreets: 'Waterworks Road, Musgrave Road, Enoggera Terrace, and Kennigo Street',
  priceRange: [
    { type: 'Character house lot', size: '400-600m²', range: '$1.0M - $1.8M (residential)', perm2: 'N/A — character protected' },
    { type: 'MDR site (no overlay)', size: '500-700m²', range: '$1.2M - $2.0M', perm2: '$2,200 - $3,000' },
    { type: 'View site (HDR/Centre)', size: '600m²+', range: '$2.0M - $4.0M+', perm2: '$3,000 - $5,000+' },
  ],
  keyDriver: 'Elevated city views, proximity to Paddington and Kelvin Grove precincts, and inner-city position with heritage character appeal',
  uniqueFactor: 'Red Hill is defined by its elevated topography — many properties enjoy panoramic views of the Brisbane CBD, river, and surrounding ranges. This natural amenity, combined with heritage Queenslander character and proximity to Paddington and Kelvin Grove, creates a unique inner-city residential proposition.',
  comparison: 'Red Hill\'s view sites command premiums that few other inner-city suburbs can match. Properties with unobstructed city views in suitable zones are among the most valuable development sites in the inner west.',
  nearbyAmenity: 'CBD and river views, Paddington Given Terrace, Kelvin Grove Urban Village, and heritage streetscapes',
});

export const auchenflowerArticles = createSuburbArticles('Auchenflower', 'auchenflower', {
  distance: '3km west of',
  keyInfra: 'Auchenflower train station provides direct CBD rail access. Wesley Hospital is a major private health facility generating sustained medical workforce demand. Coronation Drive provides a scenic route to the CBD. Proximity to Toowong Village and the University of Queensland adds lifestyle and employment appeal.',
  zones: [
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 30-50% above residential' },
    { name: 'Medium Density Residential', height: '4-6 storeys', product: 'Walk-up apartments', premium: 'Significant — 50-80% above residential' },
    { name: 'High Density Residential', height: '8-12+ storeys', product: 'Apartment buildings near station', premium: 'Very high — transit-oriented premium' },
    { name: 'Special Purpose (Hospital)', height: 'Varies', product: 'Health and medical facilities', premium: 'N/A — institutional use' },
  ],
  mainStreets: 'Coronation Drive, Milton Road, Birdwood Terrace, and Park Road',
  priceRange: [
    { type: 'Standard house (LMR)', size: '400-600m²', range: '$850K - $1.3M', perm2: '$1,800 - $2,400' },
    { type: 'MDR zone site', size: '500-700m²', range: '$1.2M - $2.0M', perm2: '$2,200 - $3,000' },
    { type: 'HDR site (near station)', size: '600-1,000m²', range: '$2.0M - $4.5M+', perm2: '$3,000 - $5,000' },
  ],
  keyDriver: 'Auchenflower train station, Wesley Hospital medical workforce demand, and Coronation Drive corridor positioning',
  uniqueFactor: 'Auchenflower is a quiet, leafy residential suburb with the advantage of direct rail access and Wesley Hospital — one of Brisbane\'s largest private hospitals. This combination of suburban tranquility and excellent connectivity makes it attractive to both developers and end-buyers seeking an inner-city lifestyle without the noise.',
  comparison: 'Auchenflower offers slightly more affordable entry points than neighbouring Milton and Toowong while benefiting from similar rail connectivity and amenity access.',
  nearbyAmenity: 'Auchenflower train station, Wesley Hospital, Coronation Drive, and Toowong Village',
});

export const duttonParkArticles = createSuburbArticles('Dutton Park', 'dutton-park', {
  distance: '3km south of',
  keyInfra: 'The new **Cross River Rail Dutton Park station** is transforming the suburb with underground rail access to the CBD in minutes. The Princess Alexandra (PA) Hospital is a major tertiary hospital generating sustained medical workforce housing demand. Proximity to the University of Queensland (across the river) attracts student and academic housing demand.',
  zones: [
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 30-50% above residential' },
    { name: 'Medium Density Residential', height: '4-6 storeys', product: 'Walk-up apartments', premium: 'Significant — 60-100% above residential' },
    { name: 'High Density Residential', height: '8-12+ storeys', product: 'Apartment buildings near CRR station', premium: 'Very high — Cross River Rail premium' },
    { name: 'Special Purpose (Hospital)', height: 'Varies', product: 'Health, research, medical uses', premium: 'N/A — institutional' },
  ],
  mainStreets: 'Annerley Road, Gladstone Road, Cornwall Street, and Ipswich Road',
  priceRange: [
    { type: 'Standard house (LMR)', size: '400-600m²', range: '$800K - $1.2M', perm2: '$1,700 - $2,200' },
    { type: 'MDR zone site', size: '500-700m²', range: '$1.1M - $1.8M', perm2: '$2,000 - $2,800' },
    { type: 'HDR site (near CRR)', size: '600-1,000m²', range: '$1.8M - $4.0M+', perm2: '$2,800 - $4,500' },
  ],
  keyDriver: 'New Cross River Rail station creating a transit-oriented development precinct, and PA Hospital driving medical workforce housing demand',
  uniqueFactor: 'Dutton Park is experiencing a generational transformation driven by the new Cross River Rail underground station. Previously a quiet, affordable suburb, it is rapidly becoming a transit-oriented development precinct. Combined with the PA Hospital health precinct — one of Queensland\'s largest employers — the suburb offers developers a compelling combination of transport, employment, and end-buyer demand.',
  comparison: 'Dutton Park offers the strongest value proposition for Cross River Rail-adjacent development sites outside of Woolloongabba. Sites near the new station are experiencing rapid price appreciation.',
  nearbyAmenity: 'Cross River Rail station, PA Hospital, UQ (via Green Bridge), and Boggo Road Urban Village',
});

export const highgateHillArticles = createSuburbArticles('Highgate Hill', 'highgate-hill', {
  distance: '2.5km south of',
  keyInfra: 'Highgate Hill\'s elevated position provides spectacular CBD and river views from many properties. Dornoch Terrace is the suburb\'s signature street with panoramic city vistas. Proximity to South Bank (walking distance) and the West End lifestyle precinct provides exceptional amenity access. Bus routes along Gladstone Road and Dornoch Terrace connect to the CBD.',
  zones: [
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 30-50% above residential' },
    { name: 'Medium Density Residential', height: '4-5 storeys', product: 'Walk-up apartments', premium: 'Significant — 50-80% above residential' },
    { name: 'High Density (limited areas)', height: '6-10+ storeys', product: 'Apartments with city views', premium: 'Very high — view premium' },
  ],
  mainStreets: 'Gladstone Road, Dornoch Terrace, Hampstead Road, and Bellevue Terrace',
  priceRange: [
    { type: 'Standard house (LMR)', size: '400-600m²', range: '$900K - $1.4M', perm2: '$1,900 - $2,600' },
    { type: 'MDR zone site', size: '500-700m²', range: '$1.3M - $2.2M', perm2: '$2,400 - $3,200' },
    { type: 'View site (HDR)', size: '600m²+', range: '$2.2M - $4.5M+', perm2: '$3,500 - $5,500+' },
  ],
  keyDriver: 'Panoramic CBD and river views, walking distance to South Bank and West End lifestyle precincts, and limited remaining development sites',
  uniqueFactor: 'Highgate Hill occupies an elevated ridge offering some of Brisbane\'s most spectacular city and river views. Properties along Dornoch Terrace and surrounding streets enjoy panoramic vistas that few suburbs can match. This natural amenity, combined with walking distance to South Bank and the West End lifestyle precinct, makes Highgate Hill a premium development address.',
  comparison: 'Highgate Hill view sites command premiums comparable to Kangaroo Point. The combination of views, South Bank proximity, and limited supply creates strong developer demand for remaining opportunities.',
  nearbyAmenity: 'CBD and river panoramas, South Bank Parklands, West End Boundary Street, and Dornoch Terrace viewpoints',
});

export const eastBrisbaneArticles = createSuburbArticles('East Brisbane', 'east-brisbane', {
  distance: '3km south-east of',
  keyInfra: 'Mowbray Park ferry terminal provides river transport access to the CBD. Proximity to Woolloongabba (Cross River Rail) and Kangaroo Point extends the influence of major infrastructure investment. The Gabba cricket ground and Woolloongabba\'s transformation create spillover demand. Bus routes along Wellington Road and Lytton Road connect to the CBD.',
  zones: [
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes', premium: 'Moderate — 30-50% above residential' },
    { name: 'Medium Density Residential', height: '4-5 storeys', product: 'Walk-up apartments', premium: 'Significant — 50-80% above residential' },
    { name: 'High Density (limited)', height: '6-10+ storeys', product: 'Apartments along main corridors', premium: 'High — Woolloongabba spillover premium' },
  ],
  mainStreets: 'Wellington Road, Lytton Road, Mowbray Terrace, and Riding Road',
  priceRange: [
    { type: 'Standard house (LMR)', size: '400-600m²', range: '$850K - $1.3M', perm2: '$1,800 - $2,400' },
    { type: 'MDR zone site', size: '500-700m²', range: '$1.1M - $1.8M', perm2: '$2,000 - $2,800' },
    { type: 'HDR site (corridor)', size: '600m²+', range: '$1.8M - $3.5M+', perm2: '$2,800 - $4,000' },
  ],
  keyDriver: 'Spillover demand from Woolloongabba\'s Cross River Rail transformation, river access via Mowbray Park ferry, and proximity to Kangaroo Point',
  uniqueFactor: 'East Brisbane is benefiting from a powerful adjacency effect — its neighbour Woolloongabba is undergoing Brisbane\'s most dramatic urban transformation with Cross River Rail and Olympic investment. As Woolloongabba site prices surge, developers are increasingly looking to East Brisbane for better-value opportunities with similar inner-city positioning.',
  comparison: 'East Brisbane offers significantly better value than Woolloongabba and Kangaroo Point while benefiting from the same infrastructure investment. This value gap is narrowing as developer demand intensifies.',
  nearbyAmenity: 'Mowbray Park ferry, Woolloongabba Cross River Rail precinct, Kangaroo Point cliffs, and Norman Park',
});

export const normanParkArticles = createSuburbArticles('Norman Park', 'norman-park', {
  distance: '4km east of',
  keyInfra: 'Norman Park train station provides direct CBD rail access. The suburb sits between East Brisbane (Woolloongabba spillover) and Coorparoo (established suburban centre). Wynnum Road provides a commercial corridor with neighbourhood retail. Proximity to Seven Hills Bushland Reserve provides green space amenity.',
  zones: [
    { name: 'Low-Medium Density Residential', height: '2-3 storeys', product: 'Townhouses, duplexes, secondary dwellings', premium: 'Moderate — 25-45% above residential' },
    { name: 'Medium Density Residential', height: '4-5 storeys', product: 'Walk-up apartments', premium: 'Significant — 50-75% above residential' },
    { name: 'Centre (Wynnum Rd)', height: '4-6 storeys', product: 'Mixed-use, neighbourhood retail + residential', premium: 'High — station precinct premium' },
  ],
  mainStreets: 'Wynnum Road, Wellington Road, Agnew Street, and Norman Avenue',
  priceRange: [
    { type: 'Standard house (LMR)', size: '400-600m²', range: '$750K - $1.1M', perm2: '$1,600 - $2,000' },
    { type: 'MDR zone site', size: '500-700m²', range: '$1.0M - $1.6M', perm2: '$1,800 - $2,500' },
    { type: 'Centre zone (Wynnum Rd)', size: '600m²+', range: '$1.4M - $2.8M+', perm2: '$2,200 - $3,500' },
  ],
  keyDriver: 'Norman Park train station, relative affordability within the inner-city rail corridor, and spillover demand from Woolloongabba/East Brisbane development boom',
  uniqueFactor: 'Norman Park is an established, family-friendly suburb with the significant advantage of a train station providing direct CBD access. As development site prices in neighbouring East Brisbane and Woolloongabba escalate, Norman Park\'s relative affordability is attracting growing developer interest — particularly for townhouse and boutique apartment projects.',
  comparison: 'Norman Park offers the most affordable rail-connected development sites in Brisbane\'s inner-east corridor. As Woolloongabba and East Brisbane prices rise, Norman Park represents the next natural development frontier.',
  nearbyAmenity: 'Norman Park train station, Wynnum Road shops, Seven Hills Bushland Reserve, and East Brisbane river precinct',
});
